import React, { useState, useEffect } from 'react';
import { Icons } from './ui/Icons';
import { signInWithGoogle, loginWithEmail, registerWithEmail, createOrganization } from '../services/firebase';

interface AuthModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialState?: 'login' | 'signup';
  initialAccountType?: 'student' | 'teacher';
}

export const AuthModal: React.FC<AuthModalProps> = ({ onClose, onSuccess, initialState = 'login', initialAccountType = 'student' }) => {
  const [isLogin, setIsLogin] = useState(initialState === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [accountType, setAccountType] = useState<'student' | 'teacher'>(initialAccountType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setIsLogin(initialState === 'login');
  }, [initialState]);

  useEffect(() => {
    setAccountType(initialAccountType);
  }, [initialAccountType]);
  
  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');

      // For teacher signup with org name, validate before Google sign-in
      if (!isLogin && accountType === 'teacher' && !orgName.trim()) {
        setError("Please enter your ministry/church name.");
        setLoading(false);
        return;
      }

      // Set flags BEFORE auth to ensure they're ready when auth listener fires
      if (!isLogin && accountType === 'teacher' && orgName.trim()) {
        sessionStorage.setItem('creatingOrganization', 'true');
      } else if (!isLogin && accountType === 'teacher') {
        sessionStorage.setItem('accountType', 'teacher');
      } else if (!isLogin && accountType === 'student') {
        sessionStorage.setItem('accountType', 'student');
      }

      const user = await signInWithGoogle();

      // If teacher account with org name, create organization immediately
      if (!isLogin && accountType === 'teacher' && orgName.trim() && user) {
        await createOrganization(user.uid, orgName);
      }

      onSuccess();
    } catch (err) {
      console.error(err);
      setError("Failed to sign in with Google.");
      // Clear the flag if auth failed
      sessionStorage.removeItem('creatingOrganization');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please fill in all fields.");
      return;
    }
    if (!isLogin && !name) {
      setError("Please enter your name.");
      return;
    }
    if (!isLogin && accountType === 'teacher' && !orgName.trim()) {
      setError("Please enter your ministry/church name.");
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Set flags BEFORE auth to ensure they're ready when auth listener fires
      if (!isLogin && accountType === 'teacher' && orgName.trim()) {
        sessionStorage.setItem('creatingOrganization', 'true');
      } else if (!isLogin && accountType === 'teacher') {
        sessionStorage.setItem('accountType', 'teacher');
      } else if (!isLogin && accountType === 'student') {
        sessionStorage.setItem('accountType', 'student');
      }

      if (isLogin) {
        await loginWithEmail(email, password);
      } else {
        // Register the user first
        const user = await registerWithEmail(name, email, password);

        // If teacher account, create organization immediately
        if (accountType === 'teacher' && orgName.trim() && user) {
          await createOrganization(user.uid, orgName);
        }
      }
      onSuccess();
    } catch (err: any) {
      console.error(err);
      // Clear the flag if auth or org creation failed
      sessionStorage.removeItem('creatingOrganization');

      if (err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError("Invalid email or password.");
      } else if (err.code === 'auth/email-already-in-use') {
        setError("Email already in use.");
      } else if (err.code === 'auth/weak-password') {
        setError("Password should be at least 6 characters.");
      } else {
        setError("Authentication failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative animate-bounce-in">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <Icons.X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
            <Icons.User className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-900">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
          <p className="text-gray-500 mt-2">
            {isLogin
              ? 'Sign in to access your studies.'
              : accountType === 'teacher'
                ? 'Create your organization and start building studies.'
                : 'Join Bible Aware today.'
            }
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-6 flex items-center gap-2">
            <Icons.X className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {/* Google Button */}
        <button 
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Continue with Google
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with email</span>
          </div>
        </div>

        <form onSubmit={handleEmailAuth} className="space-y-4">
          {!isLogin && (
            <div className="relative">
              <Icons.User className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full Name"
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                required={!isLogin}
              />
            </div>
          )}

          {!isLogin && accountType === 'teacher' && (
            <div className="relative">
              <Icons.BookOpen className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Ministry / Church Name"
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                required={!isLogin && accountType === 'teacher'}
              />
            </div>
          )}

          <div className="relative">
            <Icons.Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email Address"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
              required
            />
          </div>

          <div className="relative">
            <Icons.Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
              required
              minLength={6}
            />
          </div>

          <button 
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-blue-900 transition-colors shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Icons.Loader className="w-4 h-4 animate-spin" />}
            {isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm">
          <span className="text-gray-500">{isLogin ? "Don't have an account?" : "Already have an account?"}</span>
          <button 
            onClick={() => { setIsLogin(!isLogin); setError(''); }}
            className="ml-1 text-primary font-semibold hover:underline"
          >
            {isLogin ? 'Sign up' : 'Log in'}
          </button>
        </div>
      </div>
    </div>
  );
};